import {FC, useEffect, useRef, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {useLayout} from '../../../_metronic/layout/core'
import {showErrorAlert} from '../../helpers/alert'
import {handleApiError} from '../../utils/handleApiError'
import {useConfirmAction} from '../../hooks/utils/useConfirmAction'
import {
  cancelEngagement,
  confirmEngagement,
  getEngagement,
  unconfirmEngagement,
  updateEngagement,
} from '../../services/engagementService'
import {
  awaitingPayment,
  canEdit,
  counterpartyLabel,
  Engagement,
  formatMoney,
  statusColor,
  statusLabel,
} from './engagements'

const Field: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div className='mb-4'>
    <div className='text-muted fs-8 text-uppercase mb-1'>{label}</div>
    <div>{children}</div>
  </div>
)

// WP-X6 — engagement detail / negotiation. View the light contract; while PROPOSED either party may
// edit (which resets confirmations) and confirm; mutual confirm → AGREED (amount locks) and an
// awaiting-payment placeholder appears. NO charge is wired (X3 held).
const EngagementDetailPage: FC = () => {
  const {id} = useParams<{id: string}>()
  const history = useHistory()
  const confirmAction = useConfirmAction()
  const {config, setLayout} = useLayout()
  const restoreRef = useRef(config)

  const [engagement, setEngagement] = useState<Engagement | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [busy, setBusy] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [form, setForm] = useState({
    scope: '',
    agreedAmount: '',
    currency: 'usd',
    terms: '',
    cancellationTerms: '',
  })

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: {...config.header, display: true},
      aside: {...config.aside, display: true},
      toolbar: {...config.toolbar, display: false},
      footer: {...config.footer, display: true},
    })
    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const load = async () => {
    try {
      setLoading(true)
      setError(false)
      const data = await getEngagement(id)
      setEngagement(data)
      setForm({
        scope: data?.scope ?? '',
        agreedAmount: data?.agreedAmount != null ? String(data.agreedAmount) : '',
        currency: data?.currency ?? 'usd',
        terms: data?.terms ?? '',
        cancellationTerms: data?.cancellationTerms ?? '',
      })
    } catch (err) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const runAction = async (fn: () => Promise<any>, successMsg: string) => {
    try {
      setBusy(true)
      await fn()
      await load()
      showErrorAlert({title: 'Done', message: successMsg})
    } catch (err) {
      handleApiError(err, {
        onServerError: () =>
          showErrorAlert({
            title: 'Error',
            message: 'That action could not be completed right now.',
          }),
        onBadRequest: () =>
          showErrorAlert({title: 'Error', message: 'That action isn’t allowed at this stage.'}),
      })
    } finally {
      setBusy(false)
    }
  }

  const handleSave = () =>
    runAction(
      () =>
        updateEngagement(id, {
          scope: form.scope,
          agreedAmount: form.agreedAmount === '' ? null : Number(form.agreedAmount),
          currency: form.currency,
          terms: form.terms,
          cancellationTerms: form.cancellationTerms,
        }),
      'Proposal updated. Both parties must confirm again.'
    ).then(() => setEditMode(false))

  const handleCancel = async () => {
    const ok = await confirmAction({
      title: 'Cancel engagement',
      message: 'Cancel this engagement? This cannot be undone.',
      btnTextAction: 'Cancel engagement',
      btnTextDecline: 'Keep',
    })
    if (ok) runAction(() => cancelEngagement(id), 'Engagement cancelled.')
  }

  if (loading) return <div className='text-center py-10'>Loading…</div>
  if (error || !engagement)
    return <div className='text-center py-10 text-muted'>Couldn’t load this engagement.</div>

  const e = engagement
  const editable = canEdit(e) && (e.myRole === 'client' || e.myRole === 'expert')
  const myConfirmed = e.myRole === 'client' ? e.clientConfirmed : e.expertConfirmed
  const isTerminal = e.status === 'cancelled' || e.status === 'closed'

  return (
    <div className='engagement-detail-page'>
      <button
        type='button'
        className='nb-link-underline-500 mb-6'
        onClick={() => history.push('/engagements')}
      >
        ← My engagements
      </button>

      <div className='card p-8'>
        <div className='d-flex flex-wrap justify-content-between align-items-start mb-6 gap-3'>
          <div>
            <h1 className='mb-1'>Engagement with {counterpartyLabel(e)}</h1>
            <div className='text-muted'>You are the {e.myRole || 'observer'}</div>
          </div>
          <span className='badge' style={{backgroundColor: statusColor(e.status), color: '#fff'}}>
            {statusLabel(e.status)}
          </span>
        </div>

        {/* AGREED → awaiting-payment placeholder (no charge; X3 held) */}
        {awaitingPayment(e) && (
          <div className='alert alert-warning' role='alert'>
            <strong>Awaiting payment — {formatMoney(e.agreedAmount, e.currency)}.</strong> Terms are
            agreed and locked. Online payment isn’t available yet; no charge has been made. Settle
            with your counterpart directly for now.
          </div>
        )}

        {editable && editMode ? (
          <>
            <Field label='Scope'>
              <textarea
                className='form-control'
                rows={3}
                value={form.scope}
                onChange={(ev) => setForm({...form, scope: ev.target.value})}
              />
            </Field>
            <div className='d-flex gap-4'>
              <Field label='Proposed amount'>
                <input
                  type='number'
                  className='form-control'
                  value={form.agreedAmount}
                  onChange={(ev) => setForm({...form, agreedAmount: ev.target.value})}
                />
              </Field>
              <Field label='Currency'>
                <input
                  className='form-control'
                  value={form.currency}
                  onChange={(ev) => setForm({...form, currency: ev.target.value})}
                />
              </Field>
            </div>
            <Field label='Terms'>
              <textarea
                className='form-control'
                rows={3}
                value={form.terms}
                onChange={(ev) => setForm({...form, terms: ev.target.value})}
              />
            </Field>
            <Field label='Cancellation terms'>
              <textarea
                className='form-control'
                rows={2}
                value={form.cancellationTerms}
                onChange={(ev) => setForm({...form, cancellationTerms: ev.target.value})}
              />
            </Field>
            <div className='d-flex gap-3'>
              <button className='btn nb-btn-primary' disabled={busy} onClick={handleSave}>
                Save proposal
              </button>
              <button className='btn-secondary' onClick={() => setEditMode(false)}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <Field label='Scope'>{e.scope || '—'}</Field>
            <Field label='Amount'>
              {formatMoney(e.agreedAmount, e.currency)}
              {e.status === 'agreed' && <span className='text-muted ms-2'>(locked)</span>}
            </Field>
            <Field label='Terms'>{e.terms || '—'}</Field>
            <Field label='Cancellation terms'>{e.cancellationTerms || '—'}</Field>
            <Field label='Confirmations'>
              <div>Client: {e.clientConfirmed ? '✓ confirmed' : 'pending'}</div>
              <div>Expert: {e.expertConfirmed ? '✓ confirmed' : 'pending'}</div>
            </Field>
          </>
        )}

        {/* Actions (parties only, not while editing) */}
        {!editMode && !isTerminal && (e.myRole === 'client' || e.myRole === 'expert') && (
          <div className='d-flex flex-wrap gap-3 mt-6 pt-6 border-top'>
            {editable && (
              <button
                className='btn btn-outline btn-outline-dark'
                onClick={() => setEditMode(true)}
              >
                Edit proposal
              </button>
            )}
            {e.status === 'proposed' &&
              (myConfirmed ? (
                <button
                  className='btn btn-outline btn-outline-dark'
                  disabled={busy}
                  onClick={() =>
                    runAction(() => unconfirmEngagement(id), 'Confirmation withdrawn.')
                  }
                >
                  Withdraw confirmation
                </button>
              ) : (
                <button
                  className='btn nb-btn-primary'
                  disabled={busy}
                  onClick={() =>
                    runAction(
                      () => confirmEngagement(id),
                      'Confirmed. Agreed once both parties confirm.'
                    )
                  }
                >
                  Confirm terms
                </button>
              ))}
            <button className='btn-secondary ms-auto' disabled={busy} onClick={handleCancel}>
              Cancel engagement
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default EngagementDetailPage
